import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../../../constant/assest";
import CustomButton, {
  ButtonFont,
  ButtonSize,
  ButtonType,
} from "../../../../../component/common/CustomButton/CustomButton";
import cn from "classnames";
import UserCard from "../../../../../component/common/UserCard/UserCard";
import ActionButtons from "../../../../../component/UI/ActionButtons/ActionButtons";
import CountryCard from "../../../../../component/common/CountryCard/CountryCard";
import Pagination from "../../../../../component/UI/Pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { fetchUsers } from "../../../../../redux/slices/dashboard/actions";
import { User } from "../../../../../redux/slices/dashboard/types";
import { SubscriptionStatus } from "../../../../../constant/enums";
import TableSkeleton from "../../../../../component/common/TableSkeleton";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../../../../hooks/usePagination";

interface UserTableProps {
  searchTerm: string;
  status: SubscriptionStatus | "all";
  refresh: number;
}

const UserTable: React.FC<UserTableProps> = ({searchTerm, status, refresh}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { users: usersList, usersLoading } = useSelector(
    (state: RootState) => state.dashboard
  );
  
    const total = usersList?.total || 0;
    const totalPages = usersList?.totalPages || 0;
  
    const processedUsers = useMemo(() => {
      const users = usersList?.listing || [];
      return users.map((user) => ({
        ...user,
        full_name: user.full_name ? user.full_name.trim() : "",
      }));
    }, [usersList]);
    
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const prevSearchTerm = useRef(searchTerm);
  const prevStatus = useRef(status);

  // Fetch users when page, size, or search changes (server-side pagination)
  useEffect(() => {
    const searchChanged = prevSearchTerm.current !== searchTerm;
    const statusChanged = prevStatus.current !== status;

    if ((searchChanged || statusChanged) && pagination.pageIndex !== 0) {
      setPagination((old) => ({ ...old, pageIndex: 0 }));
      return;
    }

    const params: any = {
      limit: pagination.pageSize,
      search: searchTerm,
      status: status,
    };

    // Only add page parameter if nothing has changed that would reset the page.
    if (!searchChanged && !statusChanged) {
      params.page = pagination.pageIndex + 1;
    }

    dispatch(fetchUsers(params));

    // Update refs for the next render.
    prevSearchTerm.current = searchTerm;
    prevStatus.current = status;
  }, [dispatch, pagination, searchTerm, status, refresh]);

  // Column definitions
  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        header: "User",
        accessorKey: "full_name",
        cell: (info) => (
          <UserCard
            userName={info.getValue<string>() || 'N/A'}
            userImage={info.row.original.profile_image}
          />
        ),
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue<string>(columnId) || '';
          const b = rowB.getValue<string>(columnId) || '';
          return a.localeCompare(b);
        },
      },
      {
        header: "Email-id",
        accessorKey: "email",
        cell: (info) => <div className="email">{info.getValue<string>() || 'N/A'}</div>,
      },
      {
        header: "Country",
        accessorKey: "country_name",
        cell: (info) => {
          const countryName = info.getValue<string>() || "N/A";
          const countryEmoji = info.row.original.country_emoji;
          return (
            <CountryCard countryName={countryName} countryFlag={countryEmoji} />
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue<string>(columnId) || '';
          const b = rowB.getValue<string>(columnId) || '';
          return a.localeCompare(b);
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (info) => {
          const value = info.getValue<string>();
          const capitalizedStatus = value
            ? value.charAt(0).toUpperCase() + value.slice(1)
            : "N/A";
          return (
            <span className={`statusBadge ${capitalizedStatus.toLowerCase()}`}>
              {capitalizedStatus}
            </span>
          );
        },
      },
      {
        header: "Account",
        accessorKey: "plan_name",
        cell: (info) => info.getValue<string>() || "N/A",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ActionButtons
            // editBtnClick={() => {}}
            viewBtnClick={() =>
              navigate(`/pro-accounts/portfolios/${row.original.id}`)
            }
          />
        ),
        enableSorting: false,
      },
    ],
    [navigate]
  );

  //  React Table setup
  const table = useReactTable({
    data: processedUsers || [],
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    sortDescFirst: false,
  });

  const paginationGroup = usePagination({
    currentPageIndex: pagination.pageIndex,
    totalPages: totalPages,
  });

  const pageNumbersElements = paginationGroup.map((page, index) => {
    if (typeof page === "string") {
      return (
        <span
          key={`ellipsis-${index}`}
          className={"pageNumberBtn"}
          style={{ cursor: "default" }}
        >
          {page}
        </span>
      );
    }

    const pageIndex = page;
    return (
      <CustomButton
        key={pageIndex}
        onClick={() => table.setPageIndex(pageIndex)}
        disabled={pagination.pageIndex === pageIndex}
        btnType={
          pagination.pageIndex === pageIndex
            ? ButtonType.ACTIVENOSTYLE
            : ButtonType.NOSTYLE
        }
        btnSize={ButtonSize.NORMAL}
        btnFont={
          pagination.pageIndex === pageIndex
            ? ButtonFont.BOLD
            : ButtonFont.NORMAL
        }
        className={cn(
         'pageNumberBtn',
          pagination.pageIndex === pageIndex && "activePageNumberBtn"
        )}
      >
        {pageIndex + 1}
      </CustomButton>
    );
  });  

  return (
    <div className={'tableContainer'}>
      <div className="tableWrapper">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isActionColumn =
                    typeof header.column.columnDef.header === "string" &&
                    header.column.columnDef.header === "Action";

                  const isSorted = header.column.getIsSorted();
                  let ariaSort: "ascending" | "descending" | "none" = "none";
                  if (isSorted === "asc") ariaSort = "ascending";
                  else if (isSorted === "desc") ariaSort = "descending";

                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                      }}
                      className={isActionColumn ? "text-center" : undefined}
                      aria-sort={ariaSort} // Accessibility improvement
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() && (
                        <span className={cn("tableShorting")}>
                          <Image
                            src={
                              header.column.getIsSorted() === "asc"
                                ? ASSESTS.ASSENDING
                                : header.column.getIsSorted() === "desc"
                                ? ASSESTS.DECENDING
                                : ASSESTS.SHORT
                            }
                            alt="Sort"
                          />
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {usersLoading === "pending" ? (
              <TableSkeleton
                columns={columns.length}
                rows={pagination.pageSize}
              />
            ) : total === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={'noResultsFound'}>
                    No results found
                  </td>
                </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isActionColumn =
                      typeof cell.column.columnDef.header === "string" &&
                      cell.column.columnDef.header === "Action";
                    return (
                      <td
                        key={cell.id}
                        className={isActionColumn ? "text-center" : undefined}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Component */}
      <Pagination
        enteriesShows={`${
          total ? pagination.pageIndex * pagination.pageSize + 1 : 0
        }-${Math.min(
          (pagination.pageIndex + 1) * pagination.pageSize,
          total || 0
        )} of ${total || 0} Entries`}
        pageNumbers={[
          totalPages > 1 && (
            <CustomButton
              key="prev"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              btnType={ButtonType.NOSTYLE}
              className= "pageNumberBtn"
            >
              Prev
            </CustomButton>
          ),        
          ...pageNumbersElements,
          totalPages > 1 && (
            <CustomButton
              key="next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              btnType={ButtonType.NOSTYLE}
              className= "pageNumberBtn"
            >
              Next
            </CustomButton>
          ),
        ].filter(Boolean)}
        dropDownOptions={[
          { value: "5", label: "5" },
          { value: "10", label: "10" },
          { value: "20", label: "20" },
          { value: "25", label: "25" },
        ]}
        // dropdownOnChange={(value) => table.setPageSize(Number(value))}
        dropdownOnChange={(value: any) => {
          setPagination((old) => ({
            ...old,
            pageSize: Number(value),
            pageIndex: 0,
          }));
        }}
        dropDownPlaceholder={pagination.pageSize.toString()}
      />
    </div>
  );
};

export default React.memo(UserTable);
